import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import { makeUniqueName } from '../../core/nameHelpers';

export const MATERIAL_TEXTURE_COLOR_SPACES: Record<string, THREE.ColorSpace> = {
  map: THREE.SRGBColorSpace,
  alphaMap: THREE.NoColorSpace,
  aoMap: THREE.NoColorSpace,
  specularMap: THREE.NoColorSpace,
  specularColorMap: THREE.SRGBColorSpace,
  specularIntensityMap: THREE.NoColorSpace,
  envMap: THREE.LinearSRGBColorSpace,
  emissiveMap: THREE.SRGBColorSpace,
  bumpMap: THREE.NoColorSpace,
  normalMap: THREE.NoColorSpace,
  displacementMap: THREE.NoColorSpace,
  roughnessMap: THREE.NoColorSpace,
  metalnessMap: THREE.NoColorSpace,
  anisotropyMap: THREE.NoColorSpace,
  clearcoatMap: THREE.NoColorSpace,
  clearcoatRoughnessMap: THREE.NoColorSpace,
  clearcoatNormalMap: THREE.NoColorSpace,
  iridescenceMap: THREE.NoColorSpace,
  iridescenceThicknessMap: THREE.NoColorSpace,
  sheenColorMap: THREE.SRGBColorSpace,
  sheenRoughnessMap: THREE.NoColorSpace,
  transmissionMap: THREE.NoColorSpace,
  thicknessMap: THREE.NoColorSpace,
  matcap: THREE.LinearSRGBColorSpace,
  gradientMap: THREE.NoColorSpace,
};

export interface MaterialExtractionStorage {
  listFiles(path: ENGINE.AssetPath): Promise<{ files: Array<{ name?: string }> }>;
  uploadFile(path: ENGINE.AssetPath, blob: Blob): Promise<unknown>;
}

export interface PrepareMaterialForUniqueExtractionOptions {
  material: THREE.Material;
  materialDisplayName: string;
  assetDirectory: string;
  storage: MaterialExtractionStorage;
  textureExportPreset?: TextureExportPreset;
  createCanvas?: () => HTMLCanvasElement;
}

export interface PrepareMaterialForUniqueExtractionResult {
  materialFileName: string;
  materialOutputPath: string;
  extractedTexturePaths: string[];
}

export type TextureExportPreset = 'performance' | 'high' | 'lossless';

/** Default preset when none is passed (matches Model Viewer localStorage default). */
export const DEFAULT_TEXTURE_EXPORT_PRESET: TextureExportPreset = 'performance';
export const TEXTURE_EXPORT_PRESET_STORAGE_KEY = 'genesys-modelViewerTextureExport';
export const TEXTURE_EXPORT_PRESET_OPTIONS: Array<{
  value: TextureExportPreset;
  label: string;
  description: string;
}> = [
  {
    value: 'performance',
    label: 'Performance',
    description: 'WebP at 80% quality.',
  },
  {
    value: 'high',
    label: 'High',
    description: 'WebP at 100% quality.',
  },
  {
    value: 'lossless',
    label: 'Lossless',
    description: 'PNG only, with no WebP conversion.',
  },
];

export function parseTextureExportPreset(raw: string | null, fallback?: TextureExportPreset | null): TextureExportPreset {
  const base = fallback ?? DEFAULT_TEXTURE_EXPORT_PRESET;
  if (raw === null || raw === '') {
    return base;
  }
  try {
    const parsed: unknown = ENGINE.parseJson(raw);
    if (parsed === 'performance' || parsed === 'high' || parsed === 'lossless') {
      return parsed;
    }
  } catch {
    if (raw === 'performance' || raw === 'high' || raw === 'lossless') {
      return raw;
    }
  }
  return base;
}

export interface EncodedTextureExportResult {
  blob: Blob;
  fileExtension: '.webp' | '.png';
}

const WEBP_QUALITY_BY_PRESET: Record<Exclude<TextureExportPreset, 'lossless'>, number> = {
  performance: 0.8,
  high: 1.0,
};

export function sanitizeAssetNameSegment(name: string): string {
  const sanitized = name.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  return sanitized || 'asset';
}

function createCanvasDefault(): HTMLCanvasElement {
  if (typeof document === 'undefined') {
    throw new Error('Canvas is not available in this environment');
  }
  return document.createElement('canvas');
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> {
  return await new Promise(resolve => {
    canvas.toBlob(resolve, type, quality);
  });
}

export async function encodeCanvasForTextureExport(
  canvas: HTMLCanvasElement,
  textureExportPreset: TextureExportPreset
): Promise<EncodedTextureExportResult> {
  if (textureExportPreset === 'lossless') {
    const pngBlob = await canvasToBlob(canvas, 'image/png');
    if (!pngBlob) {
      throw new Error('Failed to convert texture to PNG');
    }
    return {
      blob: pngBlob,
      fileExtension: '.png',
    };
  }

  const webpBlob = await canvasToBlob(canvas, 'image/webp', WEBP_QUALITY_BY_PRESET[textureExportPreset]);
  if (webpBlob) {
    return {
      blob: webpBlob,
      fileExtension: '.webp',
    };
  }

  const pngBlob = await canvasToBlob(canvas, 'image/png');
  if (!pngBlob) {
    throw new Error('Failed to convert texture to WebP and PNG');
  }
  return {
    blob: pngBlob,
    fileExtension: '.png',
  };
}

export async function prepareMaterialForUniqueExtraction(
  options: PrepareMaterialForUniqueExtractionOptions
): Promise<PrepareMaterialForUniqueExtractionResult> {
  const {
    material,
    materialDisplayName,
    assetDirectory,
    storage,
    textureExportPreset = DEFAULT_TEXTURE_EXPORT_PRESET,
    createCanvas = createCanvasDefault,
  } = options;

  const materialName = sanitizeAssetNameSegment(materialDisplayName);
  const texturePathsByUuid = new Map<string, string>();
  const extractedTexturePaths: string[] = [];
  const reservedFileNames = new Set<string>();
  const directoryListing = await storage.listFiles(ENGINE.AssetPath.fromString(assetDirectory));
  for (const file of directoryListing.files) {
    if (!file?.name) {
      continue;
    }
    reservedFileNames.add(file.name.toLowerCase());
  }

  const reserveUniqueFileName = (baseName: string, extension: string): string => {
    const normalizedBaseName = sanitizeAssetNameSegment(baseName);
    const normalizedExtension = extension.toLowerCase();
    const existingBaseNames = Array.from(reservedFileNames)
      .filter(fileName => fileName.endsWith(normalizedExtension))
      .map(fileName => fileName.slice(0, -normalizedExtension.length));
    const uniqueBaseName = makeUniqueName(normalizedBaseName, existingBaseNames);
    const uniqueFileName = `${uniqueBaseName}${normalizedExtension}`;
    reservedFileNames.add(uniqueFileName.toLowerCase());
    return uniqueFileName;
  };

  const extractTextureToFile = async (texture: THREE.Texture, slotName: string): Promise<string | null> => {
    const image = texture.image as (CanvasImageSource & { width?: number, height?: number }) | undefined;
    if (!image) {
      return null;
    }

    const width = typeof image.width === 'number' && image.width > 0 ? Math.floor(image.width) : 512;
    const height = typeof image.height === 'number' && image.height > 0 ? Math.floor(image.height) : 512;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return null;
    }

    const canvas = createCanvas();
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not create canvas context for texture extraction');
    }

    context.drawImage(image, 0, 0, width, height);
    const textureBaseName = `${materialName}_${sanitizeAssetNameSegment(slotName)}`;
    const preferredExtension = textureExportPreset === 'lossless' ? '.png' : '.webp';
    const preferredFileName = reserveUniqueFileName(textureBaseName, preferredExtension);
    const { blob, fileExtension } = await encodeCanvasForTextureExport(canvas, textureExportPreset);
    const fileName = fileExtension === preferredExtension
      ? preferredFileName
      : reserveUniqueFileName(textureBaseName, fileExtension);
    const outputPath = `${assetDirectory}/${fileName}`;
    await storage.uploadFile(ENGINE.AssetPath.fromString(outputPath), blob);
    extractedTexturePaths.push(outputPath);
    return outputPath;
  };

  const materialRecord = material as unknown as Record<string, unknown>;
  for (const [slotName, colorSpace] of Object.entries(MATERIAL_TEXTURE_COLOR_SPACES)) {
    const slotTexture = materialRecord[slotName];
    if (!(slotTexture instanceof THREE.Texture)) {
      continue;
    }

    if (slotTexture instanceof ENGINE.UrlTexture) {
      slotTexture.colorSpace = colorSpace;
      slotTexture.needsUpdate = true;
      continue;
    }

    const cachedPath = texturePathsByUuid.get(slotTexture.uuid);
    const extractedPath = cachedPath ?? await extractTextureToFile(slotTexture, slotName);
    if (!extractedPath) {
      continue;
    }
    if (!cachedPath) {
      texturePathsByUuid.set(slotTexture.uuid, extractedPath);
    }

    const urlTexture = new ENGINE.UrlTexture({
      url: extractedPath,
      wrapS: slotTexture.wrapS,
      wrapT: slotTexture.wrapT,
      colorSpace,
      magFilter: slotTexture.magFilter,
      minFilter: slotTexture.minFilter,
      anisotropy: slotTexture.anisotropy,
      repeat: slotTexture.repeat.clone(),
      offset: slotTexture.offset.clone(),
      rotation: slotTexture.rotation,
    });
    urlTexture.flipY = slotTexture.flipY;
    urlTexture.center.copy(slotTexture.center);
    urlTexture.generateMipmaps = slotTexture.generateMipmaps;
    urlTexture.needsUpdate = true;
    materialRecord[slotName] = urlTexture;
  }

  if (!material.userData) {
    material.userData = {};
  }
  delete material.userData.colorSpaceFixed;
  ENGINE.MaterialUtils.fixMaterialColorSpace(material);

  const materialFileName = reserveUniqueFileName(`${materialName}_material`, ENGINE.MATERIAL_EXTENSION);
  return {
    materialFileName,
    materialOutputPath: `${assetDirectory}/${materialFileName}`,
    extractedTexturePaths,
  };
}

